// ---------------------------------------------------------------------------
// Bell Operations Console — constants & i18n
// ---------------------------------------------------------------------------
// Static configuration, translation strings and calendar labels for the
// Super Admin bell-status tracker. Kept free of React so it can be imported by
// services, utils and mock data alike.
// ---------------------------------------------------------------------------
import {BASE} from "../../../../constants/ApiConstant";

// Backend wiring. The console shares the app's API base; endpoints append
// /api/v1/admin/... . Flip DEMO to false once those endpoints are live.
//   GET  {BASE}/api/v1/admin/schools
//   GET  {BASE}/api/v1/admin/schools/{id}/bell-status?from=YYYY-MM-DD&to=YYYY-MM-DD
//   POST {BASE}/api/v1/admin/schools/{id}/ring   { period_id, date }
export const CONFIG = {
    BASE,
    DEMO: false,
    REFRESH_MS: 90000,
    LATE_THRESHOLD_SEC: 60,
};

// Status vocabulary returned per event.
// played | late | failed | offline | pending | skipped | holiday
export const FILTERABLE_STATUSES = ["played", "late", "failed", "offline"];

export const NEDIG = "०१२३४५६७८९";

export const DAYS_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const DAYS_NE = ["आइतबार", "सोमबार", "मङ्गलबार", "बुधबार", "बिहीबार", "शुक्रबार", "शनिबार"];
export const MON_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MON_NE = ["जनवरी", "फेब्रुअरी", "मार्च", "अप्रिल", "मे", "जुन", "जुलाई", "अगस्ट", "सेप्टेम्बर", "अक्टोबर", "नोभेम्बर", "डिसेम्बर"];

export const T = {
    en: {
        played: "Played", late: "Played late", failed: "Failed", offline: "Device offline", pending: "Scheduled",
        skipped: "Not scheduled", holiday: "Holiday",
        scheduled: "Rings scheduled", playedK: "Rings played", failedK: "Missed rings", offlineK: "Offline rings",
        rate: "Week reliability", ofWeek: "of this week’s bells", noFail: "No missed rings",
        device: "Controller", lastSync: "Last sync", zones: "Zones", students: "Students", uptime: "Uptime",
        period: "Period", time: "Scheduled", actual: "Rang at", delay: "Delay", strikes: "Strikes",
        zonesL: "Zones played", devState: "Device at ring time", err: "Failure detail",
        rering: "Ring now", ack: "Acknowledge", close: "Close",
        loading: "Loading the week…", emptyT: "No bell data for this week",
        emptyB: "This school has no schedule recorded between these dates.",
        errT: "Could not reach the bell service", retry: "Try again",
        noSchoolT: "Pick a school to begin", noSchoolB: "Choose any institution from the list above.",
        demo: "Showing sample data. Set ", demo2: " in the config block to load live schools.",
        ringing: "Ring command queued", acked: "Marked as reviewed", csv: "CSV downloaded",
        onTime: "on time", secLate: "s late", now: "Ringing now", today: "Today",
        week: "Week", fail1: "ring missed", failN: "rings missed",
        controller: "Controller", model: "Model", firmware: "Firmware", online: "Online", offlineW: "Offline",
        justNow: "just now", minAgo: " min ago", allOnTime: "all on time", late2: " late",
        ctrlDown: "controller was down", noSignal: "no signal", devDown: "device off", days: "days", ledger: "Bell ledger",
        updated: "Updated ",
    },
    ne: {
        played: "बज्यो", late: "ढिलो बज्यो", failed: "बजेन", offline: "यन्त्र अफलाइन", pending: "तालिकामा",
        skipped: "तालिका छैन", holiday: "बिदा",
        scheduled: "तालिकाका घण्टी", playedK: "बजेका घण्टी", failedK: "छुटेका घण्टी", offlineK: "अफलाइन घण्टी",
        rate: "साप्ताहिक भरपर्दोपन", ofWeek: "यस हप्ताका घण्टीमध्ये", noFail: "कुनै घण्टी छुटेन",
        device: "नियन्त्रक", lastSync: "अन्तिम सिंक", zones: "क्षेत्र", students: "विद्यार्थी", uptime: "अपटाइम",
        period: "पिरियड", time: "तालिका समय", actual: "बजेको समय", delay: "ढिलाइ", strikes: "प्रहार",
        zonesL: "बजेका क्षेत्र", devState: "बज्ने बेलाको अवस्था", err: "असफलताको विवरण",
        rering: "अहिले बजाउनुहोस्", ack: "हेरियो भन्ने चिन्ह", close: "बन्द",
        loading: "हप्ताको विवरण ल्याउँदै…", emptyT: "यो हप्ताको घण्टी विवरण छैन",
        emptyB: "यी मितिबीच यस विद्यालयको कुनै तालिका अभिलेख भएको छैन।",
        errT: "घण्टी सेवासँग सम्पर्क भएन", retry: "फेरि प्रयास",
        noSchoolT: "विद्यालय छान्नुहोस्", noSchoolB: "माथिको सूचीबाट कुनै संस्था छान्नुहोस्।",
        demo: "नमुना डाटा देखाइँदै। लाइभ गर्न ", demo2: " कन्फिगमा सेट गर्नुहोस्।",
        ringing: "बजाउने आदेश पठाइयो", acked: "समीक्षा भएको चिन्ह लगाइयो", csv: "CSV डाउनलोड भयो",
        onTime: "समयमै", secLate: " से. ढिलो", now: "अहिले बजिरहेको", today: "आज",
        week: "हप्ता", fail1: "घण्टी छुट्यो", failN: "घण्टी छुटे",
        controller: "नियन्त्रक", model: "मोडेल", firmware: "फर्मवेयर", online: "अनलाइन", offlineW: "अफलाइन",
        justNow: "भर्खरै", minAgo: " मिनेट अघि", allOnTime: "सबै समयमै", late2: " ढिलो",
        ctrlDown: "यन्त्र बन्द थियो", noSignal: "यन्त्र बन्द", devDown: "यन्त्र बन्द", days: "दिन", ledger: "घण्टी अभिलेख",
        updated: "अद्यावधिक ",
    },
};
